import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, StatCard, ChartCard } from '../components/ui/card';
import { RiskGaugeCard } from '../components/ui/risk-gauge-card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { UnifiedCard } from '../components/ui/unified-card';
import { DataTable } from '../components/ui/data-table';
import {
  Shield,
  Package,
  AlertTriangle,
  Lock,
  Bug,
  Target,
  AlertCircle,
  Calendar,
  FolderOpen,
  Settings,
  User,
  Cog,
  Component,
  MapPin,
  Activity,
  Archive,
  ArchiveRestore,
  RefreshCw,
  Trash2
} from 'lucide-react';
import * as echarts from 'echarts';
import {
  VulnerabilityDistribution,
  LicenseRiskDistribution,
  OverallRiskDistribution,
  ComponentRiskHeatMap,
  RiskLandscapeChart,
  SEVERITY_COLORS,
  type RiskLandscapeDataItem
} from './charts';
import { ComponentDetailsModal } from './ComponentDetailsModal';
import { LicenseObligationsModal } from './LicenseObligationsModal';
import { ScanComponent, Scan, ScanVulnerability, ScanStatus } from '@/api';
import { useTheme } from 'next-themes';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useHasPermission } from '@/hooks/useHasPermission';
import { getColorForTitle, getIconForTitle, getLicenseTyper, getRiskScoreComments, getStatusColor, toNumber } from '@/helpers/common';
import { getResultFormatBadge } from '@/helpers/scanResultUpload';
import { ArchiveScan } from './ArchiveScan';
import { RestoreScan } from './RestoreScan';
import { DeleteScan } from './DeleteScan';
import { NewScan } from './NewScan';
import { ScanReportActions } from './ScanReportActions';
import {
  getRiskCategoryForTitle,
  scoreToCategory,
  useOverallRiskDistribution,
  useVulnerabilityDistribution,
  useLicenseDistribution
} from './charts/distributionUtils';


interface BaseReportProps {
  reportType: 'scan' | 'project';
  onNavigate?: (route: string, params?: any) => void;
  onRefresh?: () => void;
  scanData?: {
    details: Scan;
    components: ScanComponent[];
    vulnerabilities?: ScanVulnerability[];
  };
}
const statusLabels = {
  [ScanStatus.PENDING]: 'Pending',
  [ScanStatus.RUNNING]: 'Running',
  [ScanStatus.COMPLETED]: 'Completed',
  [ScanStatus.FAILED]: 'Failed',
  [ScanStatus.CANCELLED]: 'Cancelled',
  [ScanStatus.CONFIGURED]: 'Configured'
};
export function BaseReport({
  reportType,
  scanData,
  onNavigate,
  onRefresh,
}: BaseReportProps) {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<any>(null);
  const [selectedLicenseId, setSelectedLicenseId] = useState<number | null>(null);
  const [isObligationsModalOpen, setIsObligationsModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rescanModalOpen, setRescanModalOpen] = useState(false);
  const isScanReport = reportType === 'scan';
  const resultFormatBadge = scanData?.details?.scanorigin === 'manual' ? getResultFormatBadge(scanData?.details?.resultformat) : null;
  const { theme, setTheme } = useTheme();
  const canExportScanReport = useEntitlement('scans', 'export');
  const canCreate = useHasPermission('scans', 'create');
  const canDelete = useHasPermission('scans', 'delete');
  const canRestore = useHasPermission('scans', 'restore');

  // Re-Scan's "View report" navigates to the new scan on this same route, so close the dialog once the report switches scans.
  useEffect(() => {
    setRescanModalOpen(false);
  }, [scanData?.details?.id]);

  const statCards: Array<{ title: string;comment?:string; value: number | string; hint?: string; disabled?: boolean }> = [];

  if (scanData?.details) {
    statCards.push(
      {
        title: "Total Components",
        value: Math.round(scanData.details.componentsfound || 0),
        hint: 'Total components detected across all scans.'
      },
      {
        title: 'Total Encryption Algorithms',
        value: Math.round(scanData.details.encryption_count || 0),
        hint: 'Total number of cryptographic algorithms detected across all scanned applications.',
        disabled: true
      }, {
      title: 'Total License Issues',
      value: Math.round(scanData.details.licenseissuesfound || 0),
      hint: 'Number of license compliance issues detected.'
    },
      {
        title: "Vulnerabilities Found",
        value: Math.round(scanData.details.vulnerabilitiesfound || 0),
        hint: 'Total number of security vulnerabilities identified.'
      },
      {
        title: "Exploitable Vulnerabilities",
        value: Math.round(scanData.details.exploitable_vulnerabilities || 0),
        hint: 'Vulnerabilities with known exploits available.'
      },
      {
        title: 'Provenance',
        value: Math.round(scanData.details.provenance_count || 0),
        hint: 'Number of component contributors whose geographic location could be determined from available metadata.',
        disabled: true
      },
      // {
      //   title: "Business Risk",
      //   value: (scanData.details.businessriskscore || 0).toFixed(2),
      //   hint: 'Represents business impact derived from criticality and exposure. Overall project risk based on license, vulnerability, and operational scores.'
      // },
      {
        title: "Overall Risk Score",
        value: (scanData.details.overallscore || 0).toFixed(2),
        comment:getRiskScoreComments('overall_risk_score', (scanData.details.overallscore || 0)),
        hint: 'Organization-level risk score combining License, Vulnerability, and Operational risk signals. Higher score = higher combined exposure.'
      },
      {
        title: "Total Technical Debt",
        value: (scanData.details.technicaldebtscore || 0).toFixed(2),
        comment:getRiskScoreComments('total_technical_debt', (scanData.details.technicaldebtscore || 0)),
        hint: 'Reflects the effort required to remediate identified issues. Combines overall risk with business impact data collected from surveys.'
      },

      {
        title: 'Operational Risk Score',
        value: (scanData.details.operationalriskscore || 0).toFixed(2),
        comment:getRiskScoreComments('operational_risk_score', (scanData.details.operationalriskscore || 0)),
        hint: 'Reflects the presence of deprecated, unmaintained, or outdated components. Considers last update dates and maintainer deprecation or archival declarations.'
      },
      {
        title: "Vulnerability Risk Score",
        value: (scanData.details.vulnerabilityscore || 0).toFixed(2),
        comment:getRiskScoreComments('vulnerability_risk_score', (scanData.details.vulnerabilityscore || 0)),
        hint: 'Reflects the presence of Critical and High severity vulnerabilities. Factors in exploit availability, EPSS likelihood, and survey inputs.'
      },
      {
        title: "License Risk Score",
        value: (scanData.details.licensescore || 0).toFixed(2),
        comment:getRiskScoreComments('license_risk_score', (scanData.details.licensescore || 0)),
        hint: 'Reflects the presence of Commercial or Strong Copyleft licenses across your applications. Critical licenses carry higher weight in the calculation.'
      },
    );
  }

  const firstRowStats = statCards.slice(0, 6);
  const secondRowStats = statCards.slice(6);

  // const licenseDistributionData: { name: string; value: number; color: string }[] = (() => {
  //   if (scanData?.components) {
  //     const categoryGroups = scanData.components.reduce((acc: Record<string, number>, component: any) => {
  //       const category = component.licensecategory || 'unknown-license';
  //       acc[category] = (acc[category] || 0) + 1;
  //       return acc;
  //     }, {});

  //     const categoryConfig = {
  //       'strong-copyleft': { name: 'Strong Copyleft', color: '#ef4444' },
  //       'unknown-license': { name: 'Unknown License', color: '#6b7280' },
  //       'weak-copyleft': { name: 'Weak Copyleft', color: '#f59e0b' },
  //       'permissive': { name: 'Permissive', color: '#22c55e' },
  //       'commercial': { name: 'Commercial', color: '#3b82f6' }
  //     };

  //     return Object.entries(categoryGroups)
  //       .map(([category, count]) => ({
  //         name: categoryConfig[category as keyof typeof categoryConfig]?.name || category,
  //         value: count,
  //         color: categoryConfig[category as keyof typeof categoryConfig]?.color || '#6b7280'
  //       }))
  //       .filter(item => item.value > 0);
  //   }

  //   return [];
  // })();

  // const exploitableComponentsData = (() => {
  //   if (scanData?.vulnerabilities) {
  //     const componentGroups: Record<string, any> = {};

  //     scanData.vulnerabilities.forEach((vuln: any) => {
  //       const componentName = vuln.components.name || 'Unknown Component';

  //       if (!componentGroups[componentName]) {
  //         componentGroups[componentName] = {
  //           componentName,
  //           vulnerabilities: [],
  //           fixAvailableCount: 0,
  //           exploitAvailableCount: 0,
  //           totalVulns: 0,
  //           epssScores: []
  //         };
  //       }

  //       componentGroups[componentName].vulnerabilities.push(vuln);
  //       componentGroups[componentName].totalVulns++;

  //       if (vuln.epssscore && vuln.epssscore > 0) {
  //         componentGroups[componentName].epssScores.push(vuln.epssscore);
  //       }

  //       if (vuln.fixavailable) {
  //         componentGroups[componentName].fixAvailableCount++;
  //       }

  //       if (vuln.exploitavailable) {
  //         componentGroups[componentName].exploitAvailableCount++;
  //       }
  //     });

  //     const fixAvailableSeries: any[] = [];
  //     const exploitAvailableSeries: any[] = [];

  //     Object.values(componentGroups).forEach((group: any) => {
  //       const avgEpssScore = group.epssScores.length > 0
  //         ? group.epssScores.reduce((sum: number, score: number) => sum + score, 0) / group.epssScores.length
  //         : 0;

  //       if (group.fixAvailableCount > 0) {
  //         fixAvailableSeries.push({
  //           name: group.componentName,
  //           value: [avgEpssScore, group.fixAvailableCount],
  //           componentName: group.componentName,
  //           totalVulns: group.totalVulns,
  //           fixCount: group.fixAvailableCount
  //         });
  //       }

  //       if (group.exploitAvailableCount > 0) {
  //         exploitAvailableSeries.push({
  //           name: group.componentName,
  //           value: [avgEpssScore, group.exploitAvailableCount],
  //           componentName: group.componentName,
  //           totalVulns: group.totalVulns,
  //           exploitCount: group.exploitAvailableCount
  //         });
  //       }
  //     });

  //     return {
  //       fixAvailable: fixAvailableSeries,
  //       exploitAvailable: exploitAvailableSeries
  //     };
  //   }

  //   return {
  //     fixAvailable: [],
  //     exploitAvailable: []
  //   };
  // })();

  const riskSpiderData = [
    {
      subject: 'Overall RIsk Score',
      score: scanData.details?.overallscore,
      fullMark: 100
    },
    {
      subject: 'Vulnerability Score',
      score: scanData.details?.vulnerabilityscore,
      fullMark: 100
    },
    {
      subject: 'License Score',
      score: scanData.details?.licensescore,
      fullMark: 100
    },
    {
      subject: 'Operational Score',
      score: scanData.details?.operationalriskscore,
      fullMark: 100
    }
  ];


  // const vulnerableComponentsData = (() => {
  //   if (scanData?.vulnerabilities) {
  //     return scanData.vulnerabilities
  //       .map((vuln, index) => {

  //         return {
  //           name: vuln.title,
  //           cvss: vuln.cvssscore || 0,
  //           epss: vuln.epssscore || 0,
  //           severity: vuln?.severity || 'Low',
  //         };
  //       })
  //       .slice(0, 15);
  //   }

  //   return [];
  // })();


  // const projectsForHooks = scanData?.details ? [
  //   {
  //     id: scanData.details.id,
  //     name: scanData.details.code || 'Scan Report',
  //     avg_vulnerability_score: scanData.details.vulnerabilityscore || 0,
  //     latest_vulnerability_score: scanData.details.vulnerabilityscore || 0,
  //     avg_license_score: scanData.details.licensescore || 0,
  //     latest_license_score: scanData.details.licensescore || 0,
  //     avg_overall_score: scanData.details.overallscore || 0,
  //     latest_overall_score: scanData.details.overallscore || 0,
  //   }
  // ] : [];

  const overallRiskData = useOverallRiskDistribution(scanData?.components || []);
  const vulnerabilityData = useVulnerabilityDistribution(scanData?.components || []);
  const licenseData = useLicenseDistribution(scanData?.components || []);

  const riskLandscapeData: RiskLandscapeDataItem[] = (scanData?.components || []).map((component) => ({
    id: toNumber(component.id),
    name: component.name || 'Unknown Component',
    description: component.version || component.description || '',
    technicaldebtscore: toNumber((component as any).technicaldebtscore ?? 0),
    vulnerabilityscore: toNumber((component as any).vulnerabilityscore ?? 0),
    licenseissuesfound: toNumber(component.total_license_issues),
    operationalriskscore: toNumber((component as any).operationalriskscore ?? 0),
    businessriskscore: toNumber((component as any).businessriskscore ?? 0),
    overallscore: toNumber((component as any).overallscore ?? 0),
    licensescore: toNumber((component as any).licensescore ?? 0),
  }));

  const heatmapRef = useRef<HTMLDivElement>(null);
  const worldMapRef = useRef<HTMLDivElement>(null);
  // const licenseDistributionChartRef = useRef<HTMLDivElement>(null);
  // const exploitableComponentsChartRef = useRef<HTMLDivElement>(null);
  // const vulnerableComponentsChartRef = useRef<HTMLDivElement>(null);
  const riskSpiderChartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!heatmapRef.current) return;

    const chart = echarts.init(heatmapRef.current, theme === 'dark' ? 'dark' : '');

    const data: any[] = [];
    const components = scanData?.components ?
      scanData.components.slice(0, 10).map(c => c.name || 'Component') :
      ['No Components'];

    for (let i = 0; i < components.length; i++) {
      let riskScore = 0;

      if (scanData?.components && scanData.components[i]) {
        const component = scanData.components[i];
        riskScore = component.risk_score;
      }

      data.push([i, 0, riskScore]);
    }

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        position: 'top',
        textStyle: {
          color: 'hsl(var(--popover-foreground))'
        },
        borderColor: 'hsl(var(--border))',
        formatter: function (params: any) {
          const componentName = components[params.data[0]];
          const riskScore = params.data[2];
          return `<div class="font-medium">${componentName}</div><div>Risk Score: ${Number(riskScore).toFixed(2)}</div>`;
        }
      },
      grid: {
        height: '60%',
        top: '10%',
        left: '15%',
        right: '10%'
      },
      xAxis: {
        type: 'category',
        data: components,
        splitArea: { show: false },
        axisLine: {
          lineStyle: {
            color: 'hsl(var(--border))'
          }
        },
        axisLabel: {
          show: false,
          color: 'hsl(var(--muted-foreground))',
          rotate: 45,
          fontSize: 10
        }
      },
      yAxis: {
        type: 'category',
        data: ['Risk Score'],
        splitArea: { show: false },
        axisLine: {
          lineStyle: {
            color: 'hsl(var(--border))'
          }
        },
        axisLabel: {
          show: false,
          color: 'hsl(var(--muted-foreground))'
        }
      },
      visualMap: {
        show: false,
        min: 0,
        max: 10,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '5%',
        inRange: { color: ['#22c55e', '#fbbf24', '#f59e0b', '#ef4444'] },
        textStyle: {
          color: 'hsl(var(--muted-foreground))'
        },
        text: ['High Risk', 'Low Risk']
      },
      series: [{
        name: 'Component Risk Score',
        type: 'heatmap',
        data: data,
        label: {
          show: true,
          color: 'hsl(var(--foreground))',
          formatter: function (params: any) {
            return params.data[2];
          }
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'hsl(var(--foreground) / 0.5)'
          }
        }
      }]
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [scanData?.components, isScanReport, theme]);

  const normalizeCountryName = (name: string): string => {
    if (!name) return '';

    const normalized = name.trim();

    const countryMappings: Record<string, string> = {
      'USA': 'United States of America',
      'US': 'United States of America',
      'United States': 'United States of America',
      'UK': 'United Kingdom',
      'UAE': 'United Arab Emirates',
      'DRC': 'Democratic Republic of the Congo',
      'Congo DRC': 'Democratic Republic of the Congo',
      'Congo (DRC)': 'Democratic Republic of the Congo',
      'Congo Republic': 'Republic of the Congo',
      'Congo (Republic)': 'Republic of the Congo',
      'South Korea': 'South Korea',
      'Korea, South': 'South Korea',
      'North Korea': 'North Korea',
      'Korea, North': 'North Korea',
      'Czech Republic': 'Czech Republic',
      'Czechia': 'Czech Republic',
      'The Netherlands': 'Netherlands',
      'Holland': 'Netherlands',
      'Ivory Coast': 'Ivory Coast',
      'Côte d\'Ivoire': 'Ivory Coast',
      'Bosnia': 'Bosnia and Herzegovina',
      'The Bahamas': 'The Bahamas',
      'Bahamas': 'The Bahamas',
      'East Timor': 'East Timor',
      'Timor-Leste': 'East Timor',
      'Burma': 'Myanmar',
      'Laos': 'Laos',
      'Russia': 'Russia',
      'Russian Federation': 'Russia',
      'Tanzania': 'United Republic of Tanzania',
      'Vietnam': 'Vietnam',
      'Viet Nam': 'Vietnam',
      'Macedonia': 'Macedonia',
      'North Macedonia': 'Macedonia',
      'Syria': 'Syria',
      'Syrian Arab Republic': 'Syria',
      'Iran': 'Iran',
      'Islamic Republic of Iran': 'Iran',
      'Venezuela': 'Venezuela',
      'Bolivarian Republic of Venezuela': 'Venezuela',
      'Moldova': 'Moldova',
      'Republic of Moldova': 'Moldova',
      'Palestine': 'Palestine',
      'Palestinian Territory': 'Palestine',
      'State of Palestine': 'Palestine',
      'West Bank': 'Palestine',
      'Gaza': 'Palestine',
      'Vatican': 'Vatican',
      'Vatican City': 'Vatican',
      'Holy See': 'Vatican',
      'Brunei': 'Brunei',
      'Brunei Darussalam': 'Brunei',
      'Eswatini': 'Swaziland',
      'Kingdom of Eswatini': 'Swaziland',
      'Swaziland': 'Swaziland',
      'Cabo Verde': 'Cape Verde',
      'The Gambia': 'Gambia',
      'Falkland Islands': 'Falkland Islands',
      'Malvinas': 'Falkland Islands',
      'French Guiana': 'French Guiana',
      'Greenland': 'Greenland',
      'Hong Kong': 'Hong Kong',
      'Macao': 'Macao',
      'Macau': 'Macao',
      'New Caledonia': 'New Caledonia',
      'Puerto Rico': 'Puerto Rico',
      'Taiwan': 'Taiwan',
      'Chinese Taipei': 'Taiwan'
    };

    if (countryMappings[normalized]) {
      return countryMappings[normalized];
    }

    const lowerNormalized = normalized.toLowerCase();
    for (const [key, value] of Object.entries(countryMappings)) {
      if (key.toLowerCase() === lowerNormalized) {
        return value;
      }
    }

    return normalized;
  };

  const processedLocationData = (() => {
    if (scanData?.details) {
      const locationCounts: Record<string, number> = {};

      const locations = [
        ...(scanData?.details.curatedlocations || []),
        ...(scanData?.details.declaredlocations || [])
      ];
      locations.forEach((location: any) => {
        const country = location.country;
        if (country) {
          const normalizedCountry = normalizeCountryName(country);
          locationCounts[normalizedCountry] = (locationCounts[normalizedCountry] || 0) + (location.count || 1);
        }
      });

      return Object.entries(locationCounts).map(([country, count]) => ({
        name: country,
        value: count
      }));
    }

    return [];
  })();

  useEffect(() => {
    if (!worldMapRef.current) return;

    const chart = echarts.init(worldMapRef.current, theme === 'dark' ? 'dark' : '');

    const values = processedLocationData.map(item => item.value);
    const maxValue = Math.max(...values, 1);
    const minValue = Math.min(...values, 0);

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          if (params.data) {
            const { name, value } = params.data;
            return `<div style="padding: 8px;">
              <strong>${name}</strong><br/>
              Count: <span style="color: #0ea5e9; font-weight: bold;">${value ?? 0}</span><br/>
         
            </div>`;
          }
          return `${params.name}<br/>No components found`;
        }
      },
      visualMap: {
        show: false,
        min: minValue,
        max: maxValue,
        left: 20,
        bottom: 30,
        text: ['High', 'Low'],
        realtime: true,
        calculable: true,
        inRange: {
          color: ['#dbeafe', '#93c5fd', '#60a5fa', '#3b82f6', '#2454adff']
        }
      },
      series: [{
        name: 'Component Distribution',
        type: 'map',
        map: 'world',
        roam: true,
        scaleLimit: {
          min: 0.8,
          max: 3
        },
        zoom: 1.1,
        emphasis: {
          label: {
            show: true,
            fontSize: 12,
            fontWeight: 'bold'
          },
          itemStyle: {
            areaColor: 'rgba(59, 130, 246, 0.5)',
            borderWidth: 1,
            shadowColor: 'rgba(0, 0, 0, 0.3)',
            shadowBlur: 10
          }
        },
        select: {
          label: {
            show: true,
            color: '#ffffff'
          },
          itemStyle: {
            areaColor: '#0a0a0aff'
          }
        },
        data: processedLocationData.map(item => ({
          name: item.name,
          value: item.value ?? "",
          emphasis: {
            itemStyle: {
              areaColor: 'rgba(59, 130, 246, 0.5)',
              color: '#0a0a0aff'
            }
          }
        }))
      }],

    };

    const loadWorldMap = async () => {
      try {
        const worldGeoJsonModule = await import('../helpers/world.geojson.json');
        const worldData = worldGeoJsonModule.default as any;

        if (worldData) {
          echarts.registerMap('world', worldData);
          chart.setOption(option);
        } else {
          throw new Error('Failed to load world map data from local file');
        }

      } catch (error) {

        const fallbackOption = {
          backgroundColor: 'transparent',
          title: {
            text: 'Component Distribution by Country',
            left: 'center'
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            }
          },
          grid: {
            left: '10%',
            right: '10%',
            bottom: '15%',
            top: '15%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: processedLocationData.map(item => item.name),
            axisLabel: {
              rotate: 45
            }
          },
          yAxis: {
            type: 'value',
            name: 'Components'
          },
          series: [{
            name: 'Components',
            type: 'bar',
            data: processedLocationData.map(item => ({
              name: item.name,
              value: item.value ?? "",
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#3b82f6' },
                  { offset: 1, color: '#1e40af' }
                ])
              }
            })),
            emphasis: {
              itemStyle: {
                color: '#0a0a0aff'
              }
            }
          }]
        };

        chart.setOption(fallbackOption);
      }
    };

    loadWorldMap();

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [processedLocationData, isScanReport, theme]);



  // useEffect(() => {
  //   if (!exploitableComponentsChartRef.current) {
  //     return;
  //   }

  //   const chart = echarts.init(exploitableComponentsChartRef.current, theme === 'dark' ? 'dark' : '');

  //   const hasData = exploitableComponentsData.fixAvailable.length > 0 || exploitableComponentsData.exploitAvailable.length > 0;



  //   const option = {
  //     backgroundColor: 'transparent',
  //     tooltip: {
  //       trigger: 'item',
  //       formatter: function (param: any) {
  //         const data = param.data;
  //         const seriesName = param.seriesName;
  //         return `
  //           <div class="font-medium">${data.componentName}</div>
  //           <div>Type: ${seriesName}</div>
  //           <div>EPSS Score: ${data.value[0].toFixed(3)}</div>
  //           <div>Count: ${data.value[1]}</div>
  //           <div>Total Vulnerabilities: ${data.totalVulns}</div>
  //         `;
  //       },

  //     },
  //     legend: {
  //       data: ['Fix Available', 'Exploit Available'],
  //       bottom: 0
  //     },
  //     grid: {
  //       left: '10%',
  //       right: '10%',
  //       top: '10%',
  //       bottom: '20%'
  //     },
  //     xAxis: {
  //       name: 'EPSS Score',
  //       nameLocation: 'middle',
  //       nameGap: 20,
  //       type: 'value'
  //     },
  //     yAxis: {
  //       name: 'Number of Vulnerabilities',
  //       nameLocation: 'middle',
  //       nameGap: 30,
  //       type: 'value'
  //     },
  //     series: [
  //       {
  //         name: 'Fix Available',
  //         type: 'scatter',
  //         data: exploitableComponentsData.fixAvailable,
  //         symbolSize: function (data: any) {
  //           return Math.max(8, Math.min(30, data[1] * 2 + 8));
  //         },
  //         itemStyle: {
  //           color: '#22c55e',
  //           opacity: 0.8
  //         },
  //         emphasis: {
  //           itemStyle: {
  //             color: '#16a34a',
  //             opacity: 1
  //           }
  //         }
  //       },
  //       {
  //         name: 'Exploit Available',
  //         type: 'scatter',
  //         data: exploitableComponentsData.exploitAvailable,
  //         symbolSize: function (data: any) {
  //           return Math.max(8, Math.min(30, data[1] * 2 + 8));
  //         },
  //         itemStyle: {
  //           color: '#ef4444',
  //           opacity: 0.8
  //         },
  //         emphasis: {
  //           itemStyle: {
  //             color: '#dc2626',
  //             opacity: 1
  //           }
  //         }
  //       }
  //     ]
  //   };

  //   chart.setOption(option);

  //   const handleResize = () => {
  //     chart.resize();
  //   };

  //   window.addEventListener('resize', handleResize);

  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //     chart.dispose();
  //   };
  // }, [exploitableComponentsData, theme]);

  // useEffect(() => {
  //   if (!vulnerableComponentsChartRef.current) return;

  //   const chart = echarts.init(vulnerableComponentsChartRef.current, theme === 'dark' ? 'dark' : '');


  //   const hasData = vulnerableComponentsData.length > 0;

  //   if (!hasData) {
  //     chart.clear();
  //     return;
  //   }

  //   const option = {
  //     tooltip: {
  //       trigger: 'item',
  //       formatter: function (param: any) {
  //         const data = param.data;
  //         return `
  //           <div class="font-medium">${data.name}</div>
  //           <div>CVSS Score: ${data.value[0].toFixed(2)}</div>
  //           <div>EPSS Score: ${data.value[1].toFixed(3)}</div>
  //           <div>Severity: ${data.severity}</div>

  //         `;
  //       },
  //     },
  //     grid: {
  //       left: '10%',
  //       right: '10%',
  //       top: '10%',
  //       bottom: '15%'
  //     },
  //     xAxis: {
  //       name: 'CVSS Score',
  //       nameLocation: 'middle',
  //       nameGap: 30,
  //       type: 'value',
  //       min: 0,
  //       max: 10
  //     },
  //     yAxis: {
  //       name: 'EPSS Score',
  //       nameLocation: 'middle',
  //       nameGap: 40,
  //       type: 'value',
  //       min: 0,
  //       max: 1
  //     },
  //     series: [{
  //       name: 'Vulnerable Components',
  //       type: 'scatter',
  //       data: vulnerableComponentsData.map(item => ({
  //         name: item.name,
  //         value: [item.cvss, item.epss],
  //         severity: item.severity,
  //       })),
  //       symbolSize: function (data: any, param: any) {
  //         const cvss = data[0] || 5;
  //         return Math.max(8, Math.min(30, cvss * 2 + 8));
  //       },
  //       itemStyle: {
  //         color: function (params: any) {
  //           const item = vulnerableComponentsData[params.dataIndex];
  //           const severity = item.severity;
  //           switch (severity) {
  //             case 'Critical': return '#dc2626';
  //             case 'High': return '#ea580c';
  //             case 'Medium': return '#d97706';
  //             case 'Low': return '#65a30d';
  //             default: return '#6b7280';
  //           }
  //         },
  //         opacity: 0.8
  //       },
  //       emphasis: {
  //         itemStyle: {
  //           opacity: 1,
  //           shadowBlur: 10,
  //           shadowColor: 'rgba(0, 0, 0, 0.3)'
  //         }
  //       }
  //     }]
  //   };

  //   chart.setOption(option);

  //   const handleResize = () => {
  //     chart.resize();
  //   };

  //   window.addEventListener('resize', handleResize);

  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //     chart.dispose();
  //   };
  // }, [vulnerableComponentsData, theme]);

  useEffect(() => {
    if (!riskSpiderChartRef.current) return;

    const chart = echarts.init(riskSpiderChartRef.current, theme === 'dark' ? 'dark' : '');

    const hasData = riskSpiderData.length > 0;

    if (!hasData) {
      chart.clear();
      return;
    }

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: function (param: any) {
          const values = param.value;
          let tooltipContent = `<div class="font-medium mb-2">${param.name}</div>`;

          riskSpiderData.forEach((item, index) => {
            const value = values[index] || 0;
            tooltipContent += `
              <div class="flex justify-between gap-4">
                <span>${item.subject}:</span>
                <span class="font-semibold">${value.toFixed(2)}</span>
              </div>
            `;
          });

          return tooltipContent;
        },
        backgroundColor: theme === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        borderColor: theme === 'dark' ? '#4b5563' : '#e5e7eb',
        textStyle: {
          color: theme === 'dark' ? '#e5e7eb' : '#374151'
        }
      },
      radar: {
        indicator: riskSpiderData.map(item => ({
          name: item.subject,
          max: item.fullMark,
          min: 0
        })),
        center: ['50%', '55%'],
        //radius: '75%',
        nameGap: 10,
        splitNumber: 5,
        name: {
          textStyle: {
            fontSize: 10,
            color: theme === 'dark' ? '#e5e7eb' : '#374151',

          }
        },
        axisLabel: {
          show: true,
          fontSize: 10,
          color: theme === 'dark' ? '#9ca3af' : '#6b7280',
          formatter: function (value: number, index: number) {
            return value.toFixed(0);
          }
        },
        splitArea: {
          show: true,
          areaStyle: {
            color: theme === 'dark'
              ? ['rgba(255, 255, 255, 0.02)', 'rgba(255, 255, 255, 0.02)', 'rgba(255, 255, 255, 0.02)', 'rgba(255, 255, 255, 0.02)']
              : ['rgba(255, 255, 255, 0.02)', 'rgba(255, 255, 255, 0.02)', 'rgba(255, 255, 255, 0.02)', 'rgba(255, 255, 255, 0.02)']
          }
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
            width: 1
          }
        },
        axisLine: {
          lineStyle: {
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
          }
        }
      },
      series: [{
        name: 'Risk Assessment',
        type: 'radar',
        data: [{
          value: riskSpiderData.map(item => item.score),
          name: 'Risk Scores',
          symbol: 'none',
          // areaStyle: {
          //   color: 'rgba(59, 130, 246, 0.3)'
          // },
          lineStyle: {
            color: '#3b82f6',
            width: 2
          },
          itemStyle: {
            color: 'transparent',
            borderWidth: 0
          }
        }],
        emphasis: {
          areaStyle: {
            color: 'rgba(59, 130, 246, 0.5)'
          }
        }
      }]
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [riskSpiderData, theme]);

  // useEffect(() => {
  //   if (!licenseDistributionChartRef.current) return;

  //   const chart = echarts.init(licenseDistributionChartRef.current, theme === 'dark' ? 'dark' : '');

  //   const hasData = licenseDistributionData && licenseDistributionData.length > 0;

  //   if (!hasData) {
  //     chart.clear();
  //     return;
  //   }

  //   const option = {
  //     backgroundColor: 'transparent',
  //     tooltip: {
  //       trigger: 'item',
  //       formatter: '{b}: {c} ({d}%)'
  //     },
  //     legend: {
  //       show: false
  //     },
  //     series: [
  //       {
  //         type: 'pie',
  //         radius: ['40%', '80%'],
  //         center: ['50%', '50%'],
  //         data: licenseDistributionData.map(item => ({
  //           value: item.value,
  //           name: item.name,
  //           itemStyle: {
  //             color: item.color
  //           }
  //         })),
  //         emphasis: {
  //           itemStyle: {
  //             shadowBlur: 10,
  //             shadowOffsetX: 0,
  //             shadowColor: 'rgba(0, 0, 0, 0.5)'
  //           }
  //         },
  //         label: {
  //           show: false
  //         }
  //       }
  //     ]
  //   };

  //   chart.setOption(option);

  //   const handleResize = () => {
  //     chart.resize();
  //   };

  //   window.addEventListener('resize', handleResize);

  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //     chart.dispose();
  //   };
  // }, [licenseDistributionData, theme]);

  const componentData = (() => {
    if (scanData?.components) {
      return scanData.components.map((component: any, index: number) => ({
        ...component,
        name: component.name || `Component ${index + 1}`,

        hasVulnerability: (component.vulnerabilities && component.vulnerabilities.length > 0) || false,

      }));
    }


    return [];
  })();


  const handleComponentClick = (component: any) => {
    setSelectedComponent(component);
    setIsDetailsModalOpen(true);
  };


  const handleModalSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleArchiveScan = (scan: Scan) => {
    setArchiveModalOpen(true);
  };
  const handleRestoreScan = (scan: Scan) => {
    setRestoreModalOpen(true);
  };
  const handleDeleteScan = (scan: Scan) => {
    setDeleteModalOpen(true);
  };
  const handleRescan = (scan: Scan) => {
    setRescanModalOpen(true);
  };


  return (
    <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>

      <div className="space-y-4">
        {scanData?.details && (
          <UnifiedCard
            isGridLayout={false}
            className="mb-4"
            listProps={{
              title:`${scanData?.details?.application_name} | ${scanData?.details?.project_name} # ${scanData?.details?.version ?? ""}`,
              //title: `${scanData?.details?.code} # ${scanData?.details?.version ?? ""}`,
              //description: scanData?.details?.description || 'Security scan details and configuration',
                description: `${scanData?.details?.code}`,
              badges: [
                {
                  label: scanData?.details?.status,
                  className: getStatusColor(scanData?.details?.status)
                },

                {
                  label: statusLabels[scanData?.details?.scanstatus] || scanData?.details?.scanstatus,
                  className: getStatusColor(scanData?.details?.scanstatus)
                },
                ...(resultFormatBadge ? [resultFormatBadge] : []),
              ],
              metrics: [


                {
                  label: `${scanData?.details?.creator_firstname || ''} ${scanData?.details?.creator_lastname || ''}`.trim() || 'System',
                  value: 'creator',
                  icon: <User className="w-4 h-4 mr-1" />
                },
                {
                  label: scanData?.details?.createdon ?
                    new Date(scanData.details.createdon).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) :
                    'N/A',
                  value: 'scan date',
                  icon: <Calendar className="w-4 h-4 mr-1" />
                }
              ],
              actionButtons: (
                <ScanReportActions
                  scan={scanData.details}
                  canExportScanReport={canExportScanReport}
                />
              ),
              actions: [
                ...(canCreate ? [{
                  label: 'Re-Scan',
                  icon: <RefreshCw className="w-4 h-4 mr-2" />,
                  onClick: () => handleRescan(scanData.details)
                }] : []),
                ...(!scanData.details.isactive
                  ? (canRestore ? [{
                      label: 'Restore Scan',
                      icon: <ArchiveRestore className="w-4 h-4 mr-2" />,
                      onClick: () => handleRestoreScan(scanData.details),
                      variant: 'default' as const
                    }] : [])
                  : (canDelete ? [{
                      label: 'Archive Scan',
                      icon: <Archive className="w-4 h-4 mr-2" />,
                      onClick: () => handleArchiveScan(scanData.details),
                      variant: 'danger' as const
                    }] : [])),
                ...(canDelete ? [{
                  label: 'Delete Scan',
                  icon: <Trash2 className="w-4 h-4 mr-2" />,
                  onClick: () => handleDeleteScan(scanData.details),
                  variant: 'danger' as const
                }] : [])
              ]
            }}
          >
            <div className="flex items-center space-x-2 flex-shrink-0">
              <div className="text-center">

                <Badge variant="outline" className="bg-muted" title="Application Name">
                  <Package className="w-4 h-4 mr-1" />
                  {scanData?.details?.application_name}
                </Badge>
              </div>
              <div className="text-center" >
                <Badge variant="outline" className="bg-muted" title="Project Name">
                  <FolderOpen className="w-4 h-4 mr-1" />
                  {scanData?.details?.project_name}
                </Badge>
              </div>
            </div>
          </UnifiedCard>
        )}

        {/* {(!scanData?.details || scanData?.details?.scanstatus !== 'completed') && (
          <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">
                    {!isScanReport ? 'Project View' : !scanData?.details ? 'No Scan Data Available' : 'Scan In Progress'}
                  </h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {!isScanReport
                      ? 'This is a project-level view. For detailed scan information, please select a completed scan.'
                      : !scanData?.details
                        ? 'No scan data is available for this report. Please check if the scan exists and try again.'
                        : 'This scan is still in progress. Report data will be available once the scan is completed.'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )} */}
      </div>

      {(scanData?.details && scanData?.details.scanstatus === 'completed') ? (
        <div className="overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {firstRowStats.map((score) => {
              return (<StatCard
                key={score.title}
                title={score.title}
                value={score.value}
                icon={getIconForTitle(score.title)}
                color={getColorForTitle(score.title, statCards.findIndex(s => s.title === score.title))}
                hint={score.hint}
                disabled={score.disabled}
              />);
            })}
          </div>

          {secondRowStats.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
              {secondRowStats.map((score) => {
                const numValue = typeof score.value === 'string' ? parseFloat(score.value) : score.value;
                const riskCategory = getRiskCategoryForTitle(score.title, numValue) as keyof typeof SEVERITY_COLORS;
                const riskColor = SEVERITY_COLORS[riskCategory];
                const riskLabel = riskCategory.charAt(0).toUpperCase() + riskCategory.slice(1);

                return (
                  <RiskGaugeCard
                    key={score.title}
                    title={score.title}
                    value={score.value}
                    riskColor={riskColor}
                    riskLabel={riskLabel}
                    hint={score.hint}
                    comment={score.comment}
                  />
                );
              })}
            </div>
          )}

          <div className="space-y-6 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <OverallRiskDistribution data={overallRiskData} onNavigate={onNavigate} projectId={scanData.details.projectid} />
              <VulnerabilityDistribution data={vulnerabilityData} onNavigate={onNavigate} projectId={scanData.details.projectid} />
              <LicenseRiskDistribution data={licenseData} onNavigate={onNavigate} projectId={scanData.details.projectid} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <RiskLandscapeChart
                data={riskLandscapeData}
              // data={(() => {
              //   if (scanData?.components) {
              //     const componentsWithScores = scanData.components.filter(component =>
              //       (component.avg_cvss_score !== undefined && component.avg_cvss_score !== null && component.avg_cvss_score > 0) ||
              //       (component.avg_epss_score !== undefined && component.avg_epss_score !== null && component.avg_epss_score > 0)
              //     );


              //     const componentsToUse = componentsWithScores.length > 0 ? componentsWithScores : scanData.components;

              //     return componentsToUse.map((component: any, index: number) => ({
              //       id: component.id || index,
              //       name: component.name || `Component ${index + 1}`,
              //       description: component.version || '',
              //       technicaldebtscore: component.technicaldebtscore || 0,
              //       vulnerabilityscore: component.vulnerabilityscore || 0,
              //       licensescore: component.licensescore || 0,
              //       operationalriskscore: component.operationalriskscore || 0,
              //       businessriskscore: component.businessriskscore || 0,
              //       overallscore: component.overallscore || 0,

              //       avg_cvss_score: component.avg_cvss_score || (component.overallscore ? component.overallscore / 10 : 0),
              //       avg_epss_score: component.avg_epss_score || (component.overallscore ? component.overallscore / 100 : 0),
              //     }));
              //   }
              //   return [];
              // })()}
              />

              <Card>
                <CardHeader>
                  <CardTitle>Risk Score Analysis</CardTitle>
                  <CardDescription>
                    Multi-dimensional risk assessment spider chart {isScanReport ? 'for this scan' : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-100 w-full relative">
                    <div ref={riskSpiderChartRef} className="h-full w-full"></div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              <ComponentRiskHeatMap
                data={scanData?.components || []}
                height={320}
              />

              <Card>
                <CardHeader>
                  <CardTitle>Component Geographic Distribution</CardTitle>
                  <CardDescription>
                    World map showing component distribution by curated locations {isScanReport ? 'in this scan' : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-96">
                    <div ref={worldMapRef} className="h-full w-full"></div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-4 italic">
                    Note: Data is extracted from GitHub; Declared Locations are user-provided and may be inexact
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 italic">
                    Note: Results will not appear in Community Edition
                  </p>
                </CardContent>
              </Card>
            </div>



          </div>


          <div className="space-y-6 mt-4">
            <DataTable
              data={componentData}
              columns={[
                {
                  key: 'name',
                  header: 'Component',
                  maxWidth: '250px',
                  sortable: true,
                  render: (value, row) => (
                    <span
                      className="font-medium dark:text-gray-200 cursor-pointer hover:underline"
                      title={value}
                      onClick={() => handleComponentClick(row)}
                    >
                      {value}
                    </span>
                  )
                },
                {
                  key: 'version',
                  header: 'Version',
                  align: 'center',
                  sortable: true,
                  render: (value) => (
                    <span className="dark:text-gray-200">{value}</span>
                  )
                },
                {
                  key: 'license_spdx_identifier',
                  header: 'License',
                  align: 'center',
                  maxWidth: '200px',
                  sortable: true,
                  render: (value, row) => (
                    <div className="flex flex-col items-center gap-1">
                      <Badge style={{ whiteSpace: "wrap" }} className="bg-muted text-muted-foreground">
                        {value || row.licensename}
                      </Badge>
                      {row.licensemainid && (
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={() => {
                            setSelectedLicenseId(row.licensemainid);
                            setIsObligationsModalOpen(true);
                          }}
                        >
                          View Obligations
                        </button>
                      )}
                    </div>
                  )
                },
                {
                  key: 'avg_cvss_score',
                  header: 'CVSS Score',
                  align: 'center',
                  sortable: true,
                  render: (value) => {
                    const score = value || 0;
                    return (
                      <span className={score >= 8.1 ? 'text-red-600 font-semibold' : score >= 6.1 ? 'text-orange-600 font-semibold' : score >= 4.1 ? 'text-yellow-600 font-semibold' : score > 0 ? 'text-green-600 font-semibold' : 'text-muted-foreground'}>
                        {score > 0 ? score.toFixed(2) : 'N/A'}
                      </span>
                    );
                  }
                },
                {
                  key: 'avg_epss_score',
                  header: 'EPSS Score',
                  align: 'center',
                  sortable: true,
                  render: (value) => {
                    const score = value || 0;
                    return (
                      <span className={score >= 8.1 ? 'text-red-600 font-semibold' : score >= 6.1 ? 'text-orange-600 font-semibold' : score >= 4.1 ? 'text-yellow-600 font-semibold' : score > 0 ? 'text-green-600 font-semibold' : 'text-muted-foreground'}>
                        {score > 0 ? score.toFixed(2) : 'N/A'}
                      </span>
                    );
                  }
                },
                {
                  key: 'maintenance_status',
                  header: 'Maintainer Status',
                  align: 'center',
                  maxWidth: '200px',
                  sortable: true,
                  render: (value, row) => (

                    <div className="flex flex-col space-y-1">
                      {row.isdeprecated && <Badge variant="destructive" className="text-xs">Deprecated</Badge>}
                      {row.isoutdated && <Badge variant="outline" className="text-xs">Outdated</Badge>}
                      {row.hasvulnerability && <Badge variant="destructive" className="text-xs">Vulnerable</Badge>}
                      <Badge style={{ whiteSpace: "wrap" }} className="bg-muted text-muted-foreground">
                        {value}
                      </Badge>
                    </div>
                  )
                },
                {
                  key: 'status',
                  header: 'License Category',
                  align: 'center',
                  render: (value, row) => (
                    <div className="flex flex-col space-y-1">
                      <Badge variant="default" className="text-xs">{getLicenseTyper(row.licensecategory)}</Badge>
                    </div>
                  )
                }
              ]}
              title="Component Analysis"
              description={`Detailed view of all components ${isScanReport ? 'in this scan' : ''} with security metrics and status`}
              itemsPerPage={15}
              maxHeight="600px"
              searchable={true}
              searchPlaceholder="Search components, versions, licenses..."
              defaultSort={{ key: 'avg_cvss_score', direction: 'desc' }}
              rowClassName={() => "hover:bg-gray-50 dark:hover:bg-gray-800"}
              emptyMessage={`No component data available ${isScanReport ? 'for this scan' : ''}`}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Scan Logs</CardTitle>
              <CardDescription>
                Scan execution logs and progress updates, please refresh page for updated logs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-900 dark:bg-slate-950 text-green-400 p-4 rounded-lg font-mono text-sm max-h-96 overflow-y-auto">
                {scanData?.details?.scanlog && scanData.details.scanlog.length > 0 ? (
                  <div className="mb-1" style={{ whiteSpace: "pre" }}>
                    {scanData.details.scanlog}
                  </div>
                  // scanData.details.scanlog.map((log: any, index: number) => (
                  //   <div key={index} className="mb-1">
                  //     <span className="text-slate-500 dark:text-slate-400">
                  //       [{log.createdon ? new Date(log.createdon).toLocaleTimeString() : 'N/A'}]
                  //     </span> {log.message || log.logs || 'No message'}
                  //   </div>
                  // ))
                ) : (
                  <div className="text-slate-500 dark:text-slate-400">
                    {scanData?.details?.scanstatus === 'pending' ? 'Scan is pending... Logs will appear here once scanning starts.' :
                      scanData?.details?.scanstatus === 'running' ? 'Scan is in progress... Logs will appear here shortly.' :
                        scanData?.details?.scanstatus === 'failed' ? 'Scan failed. No logs available.' :
                          scanData?.details?.scanstatus === 'cancelled' ? 'Scan was cancelled.' :
                            'No scan logs available.'}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* {scanData?.details?.scanstatus === 'running' && (
            <Card>
              <CardHeader>
                <CardTitle>Scan Progress</CardTitle>
                <CardDescription>
                  Current scan progress and status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{scanData?.details?.progress || 0}%</span>
                    </div>
                    <Progress value={scanData?.details?.progress || 0} className="h-3" />
                  </div>

                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">
                      Scan in progress...
                    </span>
                  </div>

                  {scanData?.details?.lastactivityon && (
                    <div className="text-xs text-muted-foreground">
                      Last activity: {new Date(scanData.details.lastactivityon).toLocaleString()}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )} */}
        </div>
      )}

      <ComponentDetailsModal
        selectedComponent={selectedComponent}
        isDetailsModalOpen={isDetailsModalOpen}
        setIsDetailsModalOpen={setIsDetailsModalOpen}
      />

      <LicenseObligationsModal
        licenseId={selectedLicenseId}
        isOpen={isObligationsModalOpen}
        onOpenChange={setIsObligationsModalOpen}
      />

      <ArchiveScan
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        onSuccess={handleModalSuccess}
        scan={scanData?.details as Scan}
      />

      <RestoreScan
        isOpen={restoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        onSuccess={handleModalSuccess}
        scan={scanData?.details as Scan}
      />

      {/* The scan no longer exists after a delete, so leave the report for the Scans list. */}
      <DeleteScan
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onSuccess={() => onNavigate?.('scans')}
        scan={scanData?.details as Scan}
      />

      {scanData?.details && canCreate && (
        <NewScan
          key={scanData.details.id}
          isOpen={rescanModalOpen}
          onClose={() => setRescanModalOpen(false)}
          onNavigate={onNavigate}
          workgroupId={scanData.details.workgroupid}
          projectId={scanData.details.projectid}
          existingScan={scanData.details}
        />
      )}
    </div>
  );
}